/**
 * NyaRegler2026Page - Guide about new K2 rules from 2026
 *
 * SEO Target: /arsredovisning/guider/nya-regler-2026
 * Primary keywords: K2 2026, nya regler årsredovisning, BFNAR ändringar 2026
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";

const WarningIcon = () => (
  <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
      clipRule="evenodd"
    />
  </svg>
);

export function NyaRegler2026Page() {
  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Guider", href: "/arsredovisning/guider" },
    { label: "Nya regler 2026" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Guider", url: "/arsredovisning/guider" },
    { name: "Nya regler 2026", url: "/arsredovisning/guider/nya-regler-2026" },
  ]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Nya K2-regler från 2026 - Vad du behöver veta",
    description:
      "Bokföringsnämnden har beslutat om ändringar i K2 som träder i kraft 2026. Läs om vad som ändras och hur det påverkar ditt företag.",
    author: {
      "@type": "Organization",
      name: "Kvanten",
    },
    publisher: {
      "@type": "Organization",
      name: "Kvanten",
      url: SEO_CONFIG.siteUrl,
    },
    datePublished: "2025-01-20",
    dateModified: "2025-01-30",
  };

  return (
    <>
      <PageMeta
        title="Nya K2-regler 2026 | Ändringar i Årsredovisning | Kvanten"
        description="Allt du behöver veta om nya K2-regler från 2026. BRF, kryptotillgångar och utländska filialer påverkas. Se vilka ändringar som gäller och när de träder i kraft."
        keywords="K2 2026, nya regler årsredovisning 2026, BFNAR ändringar, K2 BRF 2026, kryptotillgångar K2"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/guider/nya-regler-2026`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-amber-50 to-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <span>Guide</span>
            <span>•</span>
            <span>6 min läsning</span>
            <span>•</span>
            <span>Uppdaterad jan 2025</span>
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Nya K2-regler från 2026: Vad du behöver veta
          </h1>

          <p className="text-xl text-gray-600">
            Bokföringsnämnden (BFN) har beslutat om flera ändringar i K2-regelverket som träder i
            kraft för räkenskapsår som börjar 1 januari 2026 eller senare. Här sammanfattar vi vad
            som ändras.
          </p>
        </div>
      </section>

      {/* Timeline notice */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <CalendarIcon />
              <div>
                <h2 className="font-semibold text-amber-900 mb-2">När träder reglerna i kraft?</h2>
                <p className="text-amber-800">
                  De nya reglerna gäller för <strong>räkenskapsår som inleds efter 31 december 2025</strong>.
                  Det betyder att om ditt räkenskapsår följer kalenderåret (jan-dec), påverkas du
                  först för räkenskapsåret 2026 (årsredovisning som lämnas in 2027).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Innehåll</h2>
            <nav>
              <ol className="space-y-2 text-blue-600">
                <li>
                  <a href="#sammanfattning" className="hover:underline">
                    1. Sammanfattning av ändringar
                  </a>
                </li>
                <li>
                  <a href="#brf" className="hover:underline">
                    2. Bostadsrättsföreningar (BRF)
                  </a>
                </li>
                <li>
                  <a href="#krypto" className="hover:underline">
                    3. Kryptotillgångar
                  </a>
                </li>
                <li>
                  <a href="#filialer" className="hover:underline">
                    4. Utländska filialer
                  </a>
                </li>
                <li>
                  <a href="#aktierelaterade" className="hover:underline">
                    5. Aktierelaterade ersättningar
                  </a>
                </li>
                <li>
                  <a href="#vad-gora" className="hover:underline">
                    6. Vad behöver du göra?
                  </a>
                </li>
              </ol>
            </nav>
          </div>

          {/* Section 1 */}
          <section id="sammanfattning" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Sammanfattning av ändringar</h2>
            <p className="text-gray-600 mb-6">
              De viktigaste ändringarna innebär att vissa företagstyper och situationer{" "}
              <strong>inte längre får använda K2</strong> från 2026:
            </p>

            <div className="space-y-4">
              {[
                {
                  title: "Bostadsrättsföreningar (BRF)",
                  description: "BRF:er måste byta till K3 från räkenskapsår som börjar 2026.",
                  impact: "Hög",
                },
                {
                  title: "Företag med kryptotillgångar",
                  description:
                    "Företag som har kryptovalutor eller andra kryptotillgångar måste använda K3.",
                  impact: "Medium",
                },
                {
                  title: "Företag med utländska filialer",
                  description:
                    "Företag som har filialer (fast driftställe) i utlandet måste byta till K3.",
                  impact: "Låg",
                },
                {
                  title: "Aktierelaterade ersättningar",
                  description:
                    "Företag med aktieoptioner eller liknande ersättningar till anställda måste använda K3.",
                  impact: "Låg",
                },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        item.impact === "Hög"
                          ? "bg-red-100 text-red-700"
                          : item.impact === "Medium"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-green-100 text-green-700"
                      }`}
                    >
                      {item.impact} påverkan
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 2 */}
          <section id="brf" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Bostadsrättsföreningar (BRF)</h2>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <WarningIcon />
                <div>
                  <h3 className="font-semibold text-red-900">Stor förändring för BRF:er</h3>
                  <p className="text-red-800 mt-1">
                    Alla bostadsrättsföreningar måste övergå till K3 för räkenskapsår som börjar
                    2026 eller senare. Detta är den ändring som påverkar flest organisationer.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Bakgrunden till ändringen är att BRF:er har komplexa tillgångar (fastigheter) som
              kräver komponentavskrivning - något K2 inte fullt ut stödjer. BFN bedömer att K3 ger
              en mer rättvisande bild av föreningens ekonomi.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Vad innebär detta för BRF:er?</h3>
            <ul className="space-y-2 mb-4">
              {[
                "Kassaflödesanalys blir obligatorisk",
                "Komponentavskrivning ska tillämpas på fastigheten",
                "Fler och mer detaljerade noter krävs",
                "Jämförelsetal måste lämnas i noterna",
                "Eventuell uppskjuten skatt ska redovisas",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-600">
                  <span className="text-blue-500 font-bold">→</span>
                  {item}
                </li>
              ))}
            </ul>

            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-2">Rekommendation för BRF:er</h4>
              <p className="text-blue-800">
                Om din förening fortfarande använder K2, börja planera övergången till K3 redan nu.
                Kontakta er ekonomiska förvaltare eller redovisningskonsult för att säkerställa en
                smidig övergång.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="krypto" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Kryptotillgångar</h2>
            <p className="text-gray-600 mb-4">
              Företag som innehar kryptovalutor (Bitcoin, Ethereum, etc.) eller andra
              kryptotillgångar får inte längre tillämpa K2 från 2026.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Undantag</h3>
              <p className="text-gray-600">
                Om företaget endast använder kryptovaluta som betalningsmedel vid{" "}
                <em>enstaka transaktioner</em> och inte har kvar några kryptotillgångar vid
                bokslutet, kan K2 fortfarande användas.
              </p>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Varför denna ändring?</h3>
            <p className="text-gray-600 mb-4">
              K2 saknar regler för hur kryptotillgångar ska värderas och redovisas. Kryptovalutor har
              volatila värden och passar inte in i K2:s förenklade värderings­modell. K3 ger mer
              flexibilitet för korrekt redovisning.
            </p>
          </section>

          {/* Section 4 */}
          <section id="filialer" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Utländska filialer</h2>
            <p className="text-gray-600 mb-4">
              Företag som har fasta driftställen (filialer) i utlandet får inte längre använda K2.
            </p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Vad räknas som utländsk filial?</h3>
              <p className="text-gray-600 mb-2">
                Ett fast driftställe (filial) i utlandet uppstår typiskt när företaget:
              </p>
              <ul className="space-y-1 text-gray-600">
                <li>• Har ett kontor eller lokal i annat land</li>
                <li>• Har anställda som arbetar permanent i annat land</li>
                <li>• Bedriver verksamhet genom agent med fullmakt</li>
              </ul>
              <p className="mt-3 text-sm text-gray-500">
                OBS: Att ha kunder eller leverantörer i utlandet räknas normalt{" "}
                <em>inte</em> som fast driftställe.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section id="aktierelaterade" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Aktierelaterade ersättningar</h2>
            <p className="text-gray-600 mb-4">
              Företag som ger anställda ersättningar i form av aktier, aktieoptioner eller liknande
              instrument måste använda K3 från 2026.
            </p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Exempel på aktierelaterade ersättningar</h3>
              <ul className="space-y-1 text-gray-600">
                <li>• Personaloptioner</li>
                <li>• Teckningsoptioner till anställda</li>
                <li>• Aktiesparprogram</li>
                <li>• Syntetiska optioner med kontantavräkning</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section id="vad-gora" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Vad behöver du göra?</h2>

            <div className="space-y-6">
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Om du INTE påverkas av ändringarna
                </h3>
                <p className="text-gray-600">
                  De flesta aktiebolag påverkas inte. Om du är ett vanligt AB utan BRF-verksamhet,
                  krypto, utländska filialer eller aktiebaserade ersättningar kan du fortsätta
                  använda K2 som vanligt.
                </p>
              </div>

              <div className="border-l-4 border-amber-500 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">Om du påverkas</h3>
                <ol className="space-y-2 text-gray-600">
                  <li>
                    <strong>1. Inventera</strong> - Kontrollera om din verksamhet har någon av
                    företeelserna ovan
                  </li>
                  <li>
                    <strong>2. Planera</strong> - Börja planera övergång till K3 i god tid
                  </li>
                  <li>
                    <strong>3. Konsultera</strong> - Kontakta redovisningskonsult vid behov
                  </li>
                  <li>
                    <strong>4. Uppdatera system</strong> - Säkerställ att bokföringssystemet stödjer
                    K3
                  </li>
                </ol>
              </div>
            </div>

            <div className="mt-8 bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Kvanten stödjer både K2 och K3</h3>
              <p className="text-blue-800">
                Oavsett om du fortsätter med K2 eller behöver byta till K3 kan du använda Kvanten
                för att skapa din årsredovisning. Vi anpassar automatiskt noter och krav baserat på
                vilket regelverk som gäller för ditt företag.
              </p>
            </div>
          </section>

          {/* Sources */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Källor</h2>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>
                • Bokföringsnämnden (BFN):{" "}
                <a
                  href="https://www.bfn.se/andringar-i-k2-arsredovisning-for-mindre-foretag/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Ändringar i K2 årsredovisning för mindre företag
                </a>
              </li>
              <li>
                • BFNAR 2016:10 - Årsredovisning i mindre företag (K2)
              </li>
            </ul>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Behöver du hjälp med K3?</h2>
            <p className="text-amber-50 mb-6">
              Kvanten stödjer fullt ut K3-årsredovisning inklusive kassaflödesanalys och
              uppskjuten skatt. Testa gratis redan idag.
            </p>
            <Link
              to="/registrera"
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-amber-600 bg-white rounded-lg hover:bg-amber-50 transition-colors"
            >
              Kom igång gratis
            </Link>
          </section>

          {/* Related articles */}
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Relaterade guider</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "K2 vs K3 - Vilket regelverk?",
                  href: "/arsredovisning/guider/k2-vs-k3",
                  description: "Komplett jämförelse mellan regelverken",
                },
                {
                  title: "K3 Årsredovisning",
                  href: "/arsredovisning/k3",
                  description: "Allt om K3-regelverket och dess krav",
                },
              ].map((article, i) => (
                <Link
                  key={i}
                  to={article.href}
                  className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <p className="text-sm text-gray-600">{article.description}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
